import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

// ─── Types ───────────────────────────────────────────────────────────────────

export type ScheduleFrequencia = 'diaria' | 'semanal' | 'mensal';
export type ScheduleStatus = 'ok' | 'falha' | 'pendente' | 'nunca_executado';

export interface ReportSchedule {
  id: string;
  template_id: string;
  contrato_id: string;
  criado_por: string;
  ativo: boolean;
  frequencia: ScheduleFrequencia;
  dia_semana: number | null;   // 0=Dom … 6=Sáb
  dia_mes: number | null;       // 1–28
  hora_execucao: string;        // 'HH:MM:SS'
  timezone: string;
  destinatarios_emails: string[];
  destinatarios_perfis: string[];
  limite_linhas: number;
  ultimo_executado_em: string | null;
  proximo_em: string | null;
  status_ultimo: ScheduleStatus;
  erro_ultimo: string | null;
  created_at: string;
  updated_at: string;
}

export interface SchedulePayload {
  frequencia: ScheduleFrequencia;
  hora_execucao: string;
  dia_semana?: number | null;
  dia_mes?: number | null;
  timezone?: string;
  destinatarios_emails: string[];
  destinatarios_perfis: string[];
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useReportSchedule(contratoId: string, templateId: string | null) {
  const { toast } = useToast();
  const { user } = useAuth();
  const qc = useQueryClient();
  const qKey = ['emp-report-schedule', templateId];

  // Buscar agendamento do template atual (1:1 por ora)
  const { data: schedule, isLoading } = useQuery({
    queryKey: qKey,
    queryFn: async () => {
      if (!templateId) return null;
      const { data, error } = await supabase
        .from('emp_report_schedules' as never)
        .select('*')
        .eq('template_id', templateId as never)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as ReportSchedule | null;
    },
    enabled: !!templateId,
  });

  // Criar ou atualizar agendamento (upsert por template_id)
  const upsertSchedule = useMutation({
    mutationFn: async (payload: SchedulePayload) => {
      if (!templateId || !user) throw new Error('Template ou usuário não disponível');

      // Calcular primeiro proximo_em
      const proximo = calcularProximo(payload);

      if (schedule) {
        // Atualizar
        const { error } = await supabase
          .from('emp_report_schedules' as never)
          .update({
            ...payload,
            proximo_em: proximo.toISOString(),
          } as never)
          .eq('id', schedule.id as never);
        if (error) throw error;
        // Auditoria
        await supabase.from('emp_auditoria' as never).insert({
          contrato_id: contratoId,
          acao: 'schedule_updated',
          entidade: 'emp_report_schedules',
          entidade_id: schedule.id,
          dados_resumo: { frequencia: payload.frequencia, hora_execucao: payload.hora_execucao },
        } as never);
      } else {
        // Criar
        const { data: newSchedule, error } = await supabase
          .from('emp_report_schedules' as never)
          .insert({
            template_id: templateId,
            contrato_id: contratoId,
            criado_por: user.id,
            ...payload,
            proximo_em: proximo.toISOString(),
          } as never)
          .select()
          .single();
        if (error) throw error;
        // Auditoria
        await supabase.from('emp_auditoria' as never).insert({
          contrato_id: contratoId,
          acao: 'schedule_created',
          entidade: 'emp_report_schedules',
          entidade_id: (newSchedule as { id: string }).id,
          dados_resumo: { frequencia: payload.frequencia, hora_execucao: payload.hora_execucao },
        } as never);
      }
    },
    onSuccess: () => {
      toast({ title: 'Agendamento salvo', description: 'O relatório será enviado automaticamente.' });
      qc.invalidateQueries({ queryKey: qKey });
    },
    onError: (err: Error) => {
      toast({ variant: 'destructive', title: 'Erro ao salvar agendamento', description: err.message });
    },
  });

  // Ativar / suspender
  const toggleAtivo = useMutation({
    mutationFn: async (ativo: boolean) => {
      if (!schedule) return;
      const { error } = await supabase
        .from('emp_report_schedules' as never)
        .update({ ativo } as never)
        .eq('id', schedule.id as never);
      if (error) throw error;
      await supabase.from('emp_auditoria' as never).insert({
        contrato_id: contratoId,
        acao: ativo ? 'schedule_resumed' : 'schedule_paused',
        entidade: 'emp_report_schedules',
        entidade_id: schedule.id,
        dados_resumo: {},
      } as never);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: qKey }),
    onError: (err: Error) => {
      toast({ variant: 'destructive', title: 'Erro', description: err.message });
    },
  });

  // Excluir
  const deleteSchedule = useMutation({
    mutationFn: async () => {
      if (!schedule) return;
      const { error } = await supabase
        .from('emp_report_schedules' as never)
        .delete()
        .eq('id', schedule.id as never);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: 'Agendamento removido' });
      qc.invalidateQueries({ queryKey: qKey });
    },
    onError: (err: Error) => {
      toast({ variant: 'destructive', title: 'Erro ao remover', description: err.message });
    },
  });

  return { schedule, isLoading, upsertSchedule, toggleAtivo, deleteSchedule };
}

// ─── Utilitário: calcular próximo disparo ─────────────────────────────────────

export function calcularProximo(payload: SchedulePayload): Date {
  const now = new Date();
  const [hh, mm] = payload.hora_execucao.split(':').map(Number);
  const next = new Date(now);
  next.setSeconds(0, 0);
  next.setHours(hh, mm);

  if (payload.frequencia === 'diaria') {
    if (next <= now) next.setDate(next.getDate() + 1);
  } else if (payload.frequencia === 'semanal') {
    const targetDay = payload.dia_semana ?? 1;
    const current = next.getDay();
    let diff = targetDay - current;
    if (diff < 0 || (diff === 0 && next <= now)) diff += 7;
    next.setDate(next.getDate() + diff);
  } else if (payload.frequencia === 'mensal') {
    const targetDay = payload.dia_mes ?? 1;
    next.setDate(targetDay);
    if (next <= now) {
      next.setMonth(next.getMonth() + 1);
      next.setDate(targetDay);
    }
  }

  return next;
}

export const DIAS_SEMANA = [
  { value: 0, label: 'Domingo' },
  { value: 1, label: 'Segunda-feira' },
  { value: 2, label: 'Terça-feira' },
  { value: 3, label: 'Quarta-feira' },
  { value: 4, label: 'Quinta-feira' },
  { value: 5, label: 'Sexta-feira' },
  { value: 6, label: 'Sábado' },
];
